import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  IFilterData,
  IFilterDataArray,
} from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { Button } from 'react-bootstrap'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { UserAttributes } from 'api/administration/participants/types'
import AddParticipantsModal from 'components/configuration/components/lms-profiles/AddParticipantsModal'
import { ParticipantsActions } from 'pages/configuration/LmsProfiles/components/participants'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { prefixParticipants } from 'pages/configuration/LmsProfiles/constants'
import listActiveParticipants, {
  UsersSearchParams,
} from 'api/administration/participants/ListActiveParticipants'
import { SortValue } from 'models/rankmiTable.model'
import UsersTable from 'components/configuration/components/lms-profiles/UsersTable'
import { canSkipOnRowSelected } from 'helpers'
import { useEditProfile } from '../../../../context'
import { useActions } from './hooks/useActions'
import { Wrapper, HeaderActions } from './styled'

export const Participants = () => {
  const [open, setOpen] = useState(false)

  const { profile, usersAdded, setUsersAdded, removedUsers, setRemovedUsers } =
    useEditProfile()

  const tRelative = useTranslationRankmi({
    prefix: prefixParticipants,
  })

  const [selected, setSelected] = useState<number[]>([])
  const [allSelected, setAllSelected] = useState(false)
  const [search, setSearch] = useState('')
  const [filterData, setFilterData] = useState<IFilterData>()

  const { loading: loadingActions } = useActions({
    allSelected,
    filterData,
    search,
  })

  const [sort, setSort] = useState<SortValue>()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<UserAttributes[]>([])
  const [total, setTotal] = useState(0)

  const filter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'lms_profiles_id',
        condition: 'and',
        operator: 'eq',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: profile?.id,
      },
    ],
    [profile]
  )

  const addParticipantsFilter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'lms_profiles_users_lms_profile_id',
        condition: 'and',
        operator: 'not_in',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: profile?.id,
      },
    ],
    [profile]
  )

  const filterDataMerge = useMemo(
    () => ({
      filterType: 'simple',
      ...filterData,
      data: [...(filterData?.data || []), ...(filter || [])],
    }),
    [filterData, filter]
  )

  const params = useMemo(
    (): Omit<UsersSearchParams, 'page' | 'perPage'> => ({
      searchText: search,
      sort,
      usersFilter: filterDataMerge,
    }),
    [search, sort, filterDataMerge]
  )

  const fetch = useCallback(() => {
    setLoading(true)
    listActiveParticipants({
      page,
      perPage,
      ...params,
    })
      .then(({ data, total }) => {
        const newData = data.filter((user) => !removedUsers.includes(user.id))
        setList(page === 1 ? [...usersAdded, ...newData] : newData)
        setTotal(total - removedUsers.length + usersAdded.length)
      })
      .finally(() => setLoading(false))
  }, [page, params, perPage])

  useEffect(() => {
    fetch()
  }, [fetch])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
    setAllSelected(false)
  }

  const onAdd = (users: UserAttributes[]) => {
    setUsersAdded((prev) => [...prev, ...users])
    setRemovedUsers((prev) =>
      prev.filter((id) => !users.map((user) => user.id).includes(id))
    )
    setOpen(false)
    if (page === 1) {
      setList((prev) => [...users, ...prev])
      setTotal((prev) => prev + users.length)
    } else {
      setPage(1)
    }
  }

  const onRemove = (users: number[]) => {
    setRemovedUsers((prev) => [...prev, ...users])
    setList((prev) => prev.filter((user) => !users.includes(user.id)))
    setTotal((prev) => prev - users.length)
    setUsersAdded((prev) => prev.filter((user) => !users.includes(user.id)))
  }

  return (
    <div className="mt-3">
      <AddParticipantsModal
        show={open}
        onClose={() => setOpen(false)}
        onConfirm={(newUsers) => onAdd(newUsers)}
        usersAdded={usersAdded}
        filter={addParticipantsFilter}
        saving={loadingActions.add}
      />
      <Wrapper className="position-relative">
        <UsersTable
          data={{
            list,
            total,
            loading: loading || loadingActions.remove,
          }}
          onRemove={(participant) => {
            onRemove([participant.id])
            setSelected((prev) =>
              prev.filter((user) => user !== participant.id)
            )
          }}
          onRowSelected={({
            selectedRowKeys,
            allSelected,
            unselectedRowKeys,
            dispatcher,
          }) => {
            if (canSkipOnRowSelected(dispatcher)) return

            setSelected(allSelected ? unselectedRowKeys : selectedRowKeys)
            setAllSelected(allSelected)
          }}
          selected={selected}
          paginationConfig={{
            setSort,
            page,
            perPage,
            onChangeFilterData: setFilterData,
            setPage,
            setPerPage,
            filterData,
            search,
            onSearch: handleSearch,
          }}
          actions={
            <HeaderActions className="d-flex justify-content-end">
              <Button size="sm" onClick={() => setOpen(true)} className="mr-2">
                <FontAwesomeIcon icon={faPlus} />
                {tRelative('addParticipants')}
              </Button>
              <ParticipantsActions
                selected={
                  allSelected ? total - selected.length : selected.length
                }
                onRemoveSelected={() => {
                  onRemove(selected)
                  setSelected([])
                }}
              />
            </HeaderActions>
          }
        />
      </Wrapper>
    </div>
  )
}
